
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { getCitiesByState, getStateName } from "@/data/estados-cidades";
import { normalizeUrl } from "@/lib/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const EstadoPage = () => {
  const { estado } = useParams<{ estado: string }>();
  const navigate = useNavigate();
  const [estadoNome, setEstadoNome] = useState<string>("");
  const [cidades, setCidades] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!estado) {
      navigate("/cidades");
      return;
    }

    // Obter o nome do estado
    const nome = getStateName(estado);
    if (!nome) {
      console.error("Estado não encontrado:", estado);
      navigate("/cidades");
      return;
    }
    
    setEstadoNome(nome);
    
    // Obter as cidades do estado
    const cidadesDoEstado = getCitiesByState(estado);
    console.log(`Cidades de ${nome} (${estado}):`, cidadesDoEstado.length);
    setCidades(cidadesDoEstado);
    setIsLoading(false);
  }, [estado, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Carregando...</p>
      </div>
    );
  }

  // Schema para a página do estado
  const stateSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `SEO Local em ${estadoNome}`,
    "description": `Serviços de SEO Local para cidades de ${estadoNome}. Aumente a visibilidade da sua empresa no Google em todo o estado.`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "LocalUp - SEO Local para Empresas",
      "url": "https://localup.com.br"
    }
  };

  // Schema para a lista geográfica
  const geoSchema = {
    "@context": "https://schema.org",
    "@type": "State", 
    "name": estadoNome,
    "containedInPlace": {
      "@type": "Country",
      "name": "Brasil"
    },
    "containsPlace": cidades.slice(0, 100).map(cidade => ({
      "@type": "City",
      "name": cidade
    }))
  };

  return (
    <>
      <Helmet>
        <title>SEO Local em {estadoNome} | LocalUp</title>
        <meta 
          name="description" 
          content={`Serviços de SEO Local para cidades de ${estadoNome}. Aumente a visibilidade da sua empresa no Google em todo o estado.`} 
        />
        <script type="application/ld+json">
          {JSON.stringify(stateSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(geoSchema)}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <div className="py-16 px-6 md:px-12 bg-gradient-to-br from-white to-seo-light">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                
                <BreadcrumbSeparator />
                
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/cidades">Cidades</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                
                <BreadcrumbSeparator />
                
                <BreadcrumbItem>
                  <BreadcrumbPage>{estadoNome}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <h1 className="text-3xl md:text-5xl font-bold mb-6 text-seo-dark">
              SEO Local em {estadoNome}
            </h1>
            
            <p className="text-xl mb-8 text-gray-700">
              Selecione uma cidade de {estadoNome} para estratégias de SEO local personalizadas para sua região.
            </p>
          </div>
        </div>

        <section className="py-16 px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center text-seo-dark">
              Cidades de {estadoNome}
            </h2>
            
            <Card>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {cidades.map((cidade, index) => (
                    <Link 
                      key={`${estado}-${cidade}-${index}`}
                      to={`/cidade/local/${normalizeUrl(cidade)}`}
                      className="text-seo-primary hover:text-seo-secondary hover:underline p-1"
                    >
                      {cidade}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default EstadoPage;
